import { Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import {
    CheckCircleIcon,
    XCircleIcon,
    InformationCircleIcon,
    ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

function ToastifyContent({ children, type, visible }) {
    return (
        <div
            aria-live="assertive"
            className="pointer-events-none fixed inset-0 z-50 flex items-start px-4 py-6 sm:p-6"
        >
            <div className="flex w-full flex-col items-center space-y-4">
                {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                <Transition
                    show={visible}
                    as={Fragment}
                    enter="transform ease-out duration-300 transition"
                    enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                    enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                    leave="transition ease-in duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="pointer-events-auto max-w-sm overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="py-4 px-3">
                            <div className="flex items-center justify-center">
                                <div className="flex-shrink-0">
                                    {(() => {
                                        switch (type) {
                                            case "success":
                                                return (
                                                    <CheckCircleIcon
                                                        className="h-6 w-6 text-green-400"
                                                        aria-hidden="true"
                                                    />
                                                );
                                            case "error":
                                                return (
                                                    <XCircleIcon
                                                        className="h-6 w-6 text-red-400"
                                                        aria-hidden="true"
                                                    />
                                                );
                                            case "info":
                                                return (
                                                    <InformationCircleIcon
                                                        className="h-6 w-6 text-blue-400"
                                                        aria-hidden="true"
                                                    />
                                                );
                                            case "warning":
                                                return (
                                                    <ExclamationCircleIcon
                                                        className="h-6 w-6 text-yellow-400"
                                                        aria-hidden="true"
                                                    />
                                                );
                                            default:
                                                return false;
                                        }
                                    })()}
                                </div>
                                <div className="mx-3 flex-1 pt-0.5">
                                    <p className="font-medium text-gray-800">
                                        {children}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    );
}

export function Toastify({ content, type, setToastMsg, duration = 3000 }) {
    const [show, setShow] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let timeoutI, timeoutII, timeoutIII;
        if (content !== "") {
            setShow(true);
            timeoutI = setTimeout(() => {
                setVisible(true);
            }, 300);
            timeoutII = setTimeout(() => {
                setVisible(false);
                timeoutIII = setTimeout(() => {
                    setShow(false);
                    setToastMsg("");
                }, 300);
            }, duration);
        }
        return () => {
            clearTimeout(timeoutI);
            clearTimeout(timeoutII);
            clearTimeout(timeoutIII);
        };
    }, [content, duration, setToastMsg]);

    return createPortal(
        show ? (
            <ToastifyContent visible={visible} type={type}>
                {content}
            </ToastifyContent>
        ) : (
            <></>
        ),
        document.body
    );
}
